import Decimal from 'decimal.js'
import { addDays, addMinutes, getUnixTime, subDays } from 'date-fns'
import { toContextTuplesString } from 'utils/logging'

export const socketUrl = `wss://cabana-ws-proxy.fly.dev/stream`

const BIRDEYE_DIRECT_BASE_URL = 'https://public-api.birdeye.so'

export interface BirdeyeResponse<T> {
  success: boolean
  data?: T | null
}

// Make requests to Birdeye API
export async function makeApiRequest<TResponse = unknown>(
  path: string,
): Promise<BirdeyeResponse<TResponse>> {
  const response = await fetch(`/api/birdeye/${path}`)
  if (!response.ok) {
    console.error(
      `Birdeye API error: ${toContextTuplesString({
        status: response.status,
        response: await response.text(),
      })}`,
    )
    return { success: false, data: null }
  }
  return (await response.json()) as BirdeyeResponse<TResponse>
}

// Server-aware Birdeye fetcher.
// On the client: routes through /api/birdeye/* so the API key stays hidden.
// On the server (getStaticProps / getServerSideProps): hits Birdeye directly
// with the key from process.env, since same-origin routing isn't available.
export async function birdeyeFetch<TResponse = unknown>(
  path: string,
): Promise<BirdeyeResponse<TResponse>> {
  if (typeof window !== 'undefined') {
    return makeApiRequest<TResponse>(path)
  }

  const apiKey = process.env.BIRDEYE_API_KEY
  if (!apiKey) {
    console.error('BIRDEYE_API_KEY missing for server-side Birdeye request')
    return { success: false, data: null }
  }

  try {
    const response = await fetch(`${BIRDEYE_DIRECT_BASE_URL}/${path}`, {
      headers: { 'X-API-KEY': apiKey, 'x-chain': 'solana' },
    })
    if (!response.ok) {
      console.error(
        `Birdeye API error (server): ${toContextTuplesString({
          status: response.status,
          response: await response.text(),
          path,
        })}`,
      )
      return { success: false, data: null }
    }
    return (await response.json()) as BirdeyeResponse<TResponse>
  } catch (e) {
    console.error(`failed Birdeye server fetch: path=${path}`, e)
    return { success: false, data: null }
  }
}

const RESOLUTION_MAPPING: Record<string, string> = {
  '1': '1m',
  '3': '3m',
  '5': '5m',
  '15': '15m',
  '30': '30m',
  '60': '1H',
  '120': '2H',
  '240': '4H',
  '1D': '1D',
  '1W': '1W',
}

export function parseResolution(resolution: string) {
  if (!resolution || !RESOLUTION_MAPPING[resolution])
    return RESOLUTION_MAPPING[0]

  return RESOLUTION_MAPPING[resolution]
}

export function getNextBarTime(lastBarTime: number, resolution = '1D') {
  const lastCharacter = resolution.slice(-1)
  let nextBarTime: number

  switch (true) {
    case lastCharacter === 'W':
      nextBarTime = addDays(lastBarTime, 7).getTime()
      break

    case lastCharacter === 'D':
      nextBarTime = addDays(lastBarTime, 1).getTime()
      break

    default:
      nextBarTime = addMinutes(lastBarTime, 1).getTime()
      break
  }

  return nextBarTime
}

export const SUBSCRIPT_NUMBER_MAP: Record<number, string> = {
  4: '₄',
  5: '₅',
  6: '₆',
  7: '₇',
  8: '₈',
  9: '₉',
  10: '₁₀',
  11: '₁₁',
  12: '₁₂',
  13: '₁₃',
  14: '₁₄',
  15: '₁₅',
}

export const calcPricePrecision = (num: number | string) => {
  if (!num) return 8

  switch (true) {
    case Math.abs(+num) < 0.00000000001:
      return 16

    case Math.abs(+num) < 0.000000001:
      return 14

    case Math.abs(+num) < 0.0000001:
      return 12

    case Math.abs(+num) < 0.00001:
      return 10

    case Math.abs(+num) < 0.05:
      return 6

    case Math.abs(+num) < 1:
      return 4

    case Math.abs(+num) < 20:
      return 3

    default:
      return 2
  }
}

export const formatPrice = (
  num: number,
  precision?: number,
  gr0 = true,
): string => {
  if (!num) {
    return num.toString()
  }

  if (!precision) {
    precision = calcPricePrecision(+num)
  }

  let formated: string = new Decimal(num).toFixed(precision)

  if (formated.match(/^0\.[0]+$/g)) {
    formated = formated.replace(/\.[0]+$/g, '')
  }

  if (gr0 && formated.match(/\.0{4,15}[1-9]+/g)) {
    const match = formated.match(/\.0{4,15}/g)
    if (!match) return ''
    const matchString: string = match[0].slice(1)
    formated = formated.replace(
      /\.0{4,15}/g,
      `.0${SUBSCRIPT_NUMBER_MAP[matchString.length]}`,
    )
  }

  return formated
}

export type SwapChartDataItem = {
  time: number
  price: number
  inputTokenPrice: number
  outputTokenPrice: number
}

export interface HistoryPrice {
  items: readonly {
    unixTime: number
    value: number
  }[]
}

export const fetchSwapChartPrices = async (
  inputMint: string | undefined,
  outputMint: string | undefined,
  daysToShow: string,
  supportedQuoteMints: string[],
) => {
  if (!inputMint || !outputMint) return []
  const interval = daysToShow === '1' ? '30m' : daysToShow === '7' ? '1H' : '4H'
  const now = new Date()
  const queryEnd = getUnixTime(now)
  const queryStart = getUnixTime(subDays(now, parseInt(daysToShow)))
  const quoteMint = supportedQuoteMints.includes(outputMint)
    ? outputMint
    : inputMint
  const baseMint = quoteMint === outputMint ? inputMint : outputMint
  const inputQueryParams = new URLSearchParams({
    address: baseMint,
    address_type: 'token',
    type: interval,
    time_from: String(queryStart),
    time_to: String(queryEnd),
  })
  const inputQuery = `defi/history_price?${inputQueryParams.toString()}`
  const outputQueryParams = new URLSearchParams({
    address: quoteMint,
    address_type: 'token',
    type: interval,
    time_from: String(queryStart),
    time_to: String(queryEnd),
  })
  const outputQuery = `defi/history_price?${outputQueryParams.toString()}`
  try {
    const [inputResponse, outputResponse] = await Promise.all([
      makeApiRequest<HistoryPrice>(inputQuery),
      makeApiRequest<HistoryPrice>(outputQuery),
    ])

    if (
      inputResponse.success &&
      inputResponse?.data?.items?.length &&
      outputResponse.success &&
      outputResponse?.data?.items?.length
    ) {
      const parsedData: SwapChartDataItem[] = []
      const inputData = inputResponse.data.items
      const outputData = outputResponse.data.items

      for (const item of inputData) {
        const outputDataItem = outputData.find(
          (data) => data.unixTime === item.unixTime,
        )

        const curentTimestamp = Date.now() / 1000

        if (outputDataItem && item.unixTime <= curentTimestamp) {
          parsedData.push({
            time: Math.floor(item.unixTime * 1000),
            price: item.value / outputDataItem.value,
            inputTokenPrice: item.value,
            outputTokenPrice: outputDataItem.value,
          })
        }
      }
      return parsedData
    } else return []
  } catch (e) {
    console.error('failed to fetch swap chart data from birdeye', e)
    return []
  }
}
